import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, RefreshCw, X, Search, ChevronRight, Calculator,
         Lock, Package, FileText, BarChart2, Loader2,
         AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSociete } from '../context/SocieteContext';
import { SidePanel } from '../components/SidePanel';
import * as api from '../api';

const CLE_OPTIONS = [
  { value: 'VALEUR',     label: 'Ad Valorem',  desc: 'Proportionnel au prix d\'achat' },
  { value: 'EGALITAIRE', label: 'Égalitaire',  desc: 'Parts égales entre matériels' },
];

const TYPES_CHARGE = ['FRET','DOUANE','TRANSIT','ATELIER','MATERIEL'];
const TYPE_COLORS = {
  FRET:      { bg: '#EFF6FF', color: '#1D4ED8' },
  DOUANE:    { bg: '#FEF3C7', color: '#92400E' },
  TRANSIT:   { bg: '#ECFDF5', color: '#065F46' },
  ATELIER:   { bg: '#F5F3FF', color: '#6D28D9' },
  MATERIEL:  { bg: '#F9FAFB', color: '#374151' },
};

const STATUT_CFG = {
  ouvert:  { label: 'Ouvert',   bg: '#EFF6FF', color: '#1D4ED8' },
  cloture: { label: 'Clôturé',  bg: '#F0FDF4', color: '#15803D' },
};

function fmt(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function StatutBadge({ statut }) {
  const cfg = STATUT_CFG[statut] || STATUT_CFG.ouvert;
  return (
    <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
      style={{ background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
  );
}

function TypeBadge({ type }) {
  const cfg = TYPE_COLORS[type] || { bg: '#F3F4F6', color: '#374151' };
  return (
    <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
      style={{ background: cfg.bg, color: cfg.color }}>{type}</span>
  );
}

// ─── Formulaire création dossier (side-panel) ────────────────────────────────
function CreateDossierPanel({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ libelle: '', cle_repartition: 'VALEUR', date_arrivee: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.libelle.trim()) { toast.error('Libellé requis'); return; }
    setSaving(true);
    try {
      const r = await api.createDossierRevient({ ...form, date_arrivee: form.date_arrivee || null });
      toast.success(`Dossier ${r.data.numero} créé`);
      onCreated(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <SidePanel open={open} onOpenChange={v => !v && onClose()} title="Nouveau dossier de revient"
      footer={
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
            Annuler
          </button>
          <button form="create-dossier-form" type="submit" disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: 'var(--primary-solid)' }}>
            {saving ? <Loader2 size={14} className="animate-spin inline" /> : 'Créer le dossier'}
          </button>
        </div>
      }>
      <form id="create-dossier-form" onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide block mb-1.5" style={{ color: 'var(--text-muted)' }}>Libellé *</label>
          <input value={form.libelle} onChange={e => setForm(f => ({ ...f, libelle: e.target.value }))}
            placeholder="Ex: Conteneur HAPAG-LLOYD Oct 2026"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none"
            style={{ borderColor: 'var(--border)' }} />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide block mb-1.5" style={{ color: 'var(--text-muted)' }}>Clé de répartition</label>
          <div className="flex gap-2">
            {CLE_OPTIONS.map(opt => (
              <button key={opt.value} type="button"
                onClick={() => setForm(f => ({ ...f, cle_repartition: opt.value }))}
                className="flex-1 px-3 py-2.5 rounded-lg border text-sm text-left transition-colors"
                style={{
                  borderColor: form.cle_repartition === opt.value ? 'var(--primary-solid)' : 'var(--border)',
                  background: form.cle_repartition === opt.value ? 'var(--primary-50)' : 'white',
                  color: form.cle_repartition === opt.value ? 'var(--primary-solid)' : 'var(--text)',
                }}>
                <div className="font-semibold text-sm">{opt.label}</div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide block mb-1.5" style={{ color: 'var(--text-muted)' }}>Date d'arrivée</label>
          <input type="date" value={form.date_arrivee} onChange={e => setForm(f => ({ ...f, date_arrivee: e.target.value }))}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none"
            style={{ borderColor: 'var(--border)' }} />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide block mb-1.5" style={{ color: 'var(--text-muted)' }}>Notes</label>
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            rows={3} placeholder="Informations complémentaires…"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none resize-none"
            style={{ borderColor: 'var(--border)' }} />
        </div>
      </form>
    </SidePanel>
  );
}

// ─── Constantes thèmes ───────────────────────────────────────────────────────
const THEMES = {
  ACHATS_PURS:       { label: 'Achats Purs',              types: ['MATERIEL'],            color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE' },
  FRAIS_APPROCHE:    { label: 'Frais d\'Approche',         types: ['FRET','DOUANE','TRANSIT'], color: '#C2410C', bg: '#FFF7ED', border: '#FED7AA' },
  FRAIS_PREPARATION: { label: 'Frais de Préparation',      types: ['ATELIER'],              color: '#15803D', bg: '#F0FDF4', border: '#BBF7D0' },
};

function themeOf(type_charge) {
  for (const [key, th] of Object.entries(THEMES)) {
    if (th.types.includes(type_charge)) return key;
  }
  return null;
}

function pct(num, den) {
  if (!den) return '—';
  return (num / den * 100).toFixed(2) + ' %';
}

// ─── Panel détail dossier ─────────────────────────────────────────────────────
function DossierPanel({ dossier_revient_id, open, onClose, onRefresh }) {
  const [doc, setDoc]             = useState(null);
  const [loading, setLoading]     = useState(true);
  const [recalculating, setRecalculating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.getDossierRevient(dossier_revient_id); setDoc(r.data); }
    catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [dossier_revient_id]);

  useEffect(() => { if (open && dossier_revient_id) load(); }, [load, open, dossier_revient_id]);

  const [libelle, setLibelle] = useState('');

  useEffect(() => {
    if (doc) setLibelle(doc.libelle || '');
  }, [doc?.libelle]);

  const saveLibelle = async () => {
    if (libelle === doc?.libelle) return;
    try {
      await api.updateDossierRevient(dossier_revient_id, { libelle });
      onRefresh();
    } catch { toast.error('Erreur sauvegarde'); }
  };

  const handleRecalculer = async () => {
    if (!window.confirm('Recalculer le coût de revient ? Les lignes existantes seront recréées.')) return;
    setRecalculating(true);
    try {
      const r = await api.recalculerDossier(dossier_revient_id);
      toast.success(`Recalcul terminé — ${r.data.nb_lignes} imputations`);
      load(); onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setRecalculating(false); }
  };

  const handleCloturer = async () => {
    if (!window.confirm('Clôturer ce dossier ? Plus aucune modification ne sera possible.')) return;
    try { await api.cloturerDossierRevient(dossier_revient_id); toast.success('Clôturé'); load(); onRefresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const [panelTab, setPanelTab] = useState('factures');

  // Reset tab à l'ouverture d'un nouveau dossier
  useEffect(() => { if (open) setPanelTab('factures'); }, [open, dossier_revient_id]);

  // Calcul nouveau PMP pour les pièces détachées
  const computedPieces = React.useMemo(() => {
    if (!doc) return [];
    const pieces = doc.articles_pieces || [];
    const byTheme = { FRAIS_APPROCHE: 0, FRAIS_PREPARATION: 0 };
    for (const f of (doc.factures || [])) {
      const th = themeOf(f.type_charge);
      if (th && th !== 'ACHATS_PURS') byTheme[th] = (byTheme[th] || 0) + Number(f.montant_ht || 0);
    }
    const totalHtAchats = pieces.reduce((s, p) => s + Number(p.montant_ht || 0), 0) || 1;
    const totalFrais = byTheme.FRAIS_APPROCHE + byTheme.FRAIS_PREPARATION;

    return pieces.map(p => {
      const montantHt   = Number(p.montant_ht || 0);
      const ratio       = doc.cle_repartition === 'EGALITAIRE'
        ? 1 / (pieces.length || 1)
        : montantHt / totalHtAchats;
      const fraisImputes = totalFrais * ratio;
      const pmpActuel   = Number(p.pmp_actuel || 0);
      const stockActuel = Number(p.stock_actuel || 0);
      const qty         = Number(p.quantite || 1);
      const nouveauPmp  = (stockActuel + qty) > 0
        ? (pmpActuel * stockActuel + montantHt + fraisImputes) / (stockActuel + qty)
        : (montantHt + fraisImputes) / qty;
      return { ...p, fraisImputes, nouveauPmp };
    });
  }, [doc]);
  const computed = React.useMemo(() => {
    if (!doc) return {};
    const factures  = doc.factures || [];
    const materiels = doc.materiels || [];
    const lignes    = doc.lignes || [];
    const cle       = doc.cle_repartition || 'VALEUR';

    const byTheme = { ACHATS_PURS: 0, FRAIS_APPROCHE: 0, FRAIS_PREPARATION: 0 };
    for (const f of factures) {
      const th = themeOf(f.type_charge);
      if (th) byTheme[th] += Number(f.montant_ht || 0);
    }
    const total         = byTheme.ACHATS_PURS + byTheme.FRAIS_APPROCHE + byTheme.FRAIS_PREPARATION;
    const totalPrixAchat = materiels.reduce((s, m) => s + Number(m.prix_achat || 0), 0);
    const nbMats        = materiels.length || 1;
    const hasLignes     = lignes.length > 0;

    const ventilation = materiels.map(m => {
      const prixAchat = Number(m.prix_achat || 0);

      let approche, preparation;

      if (hasLignes) {
        // Source de vérité : dossier_revient_lignes (après recalcul)
        approche    = lignes.filter(l => l.materiel_id === m.materiel_id && THEMES.FRAIS_APPROCHE.types.includes(l.type_charge))
                            .reduce((s, l) => s + Number(l.montant_impute || 0), 0);
        preparation = lignes.filter(l => l.materiel_id === m.materiel_id && THEMES.FRAIS_PREPARATION.types.includes(l.type_charge))
                            .reduce((s, l) => s + Number(l.montant_impute || 0), 0);
      } else {
        // Prorata live avant premier recalcul
        const ratio = cle === 'EGALITAIRE'
          ? 1 / nbMats
          : (totalPrixAchat > 0 ? prixAchat / totalPrixAchat : 0);
        approche    = byTheme.FRAIS_APPROCHE    * ratio;
        preparation = byTheme.FRAIS_PREPARATION * ratio;
      }

      const cout_revient = prixAchat + approche + preparation;
      return { ...m, approche, preparation, cout_revient_calc: cout_revient };
    });

    return { byTheme, total, ventilation };
  }, [doc]);

  const footer = doc ? (
    <div className="flex items-center justify-between">
      {doc.statut === 'ouvert' ? (
        <button onClick={handleCloturer}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border"
          style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
          <Lock size={12} /> Clôturer le dossier
        </button>
      ) : <StatutBadge statut={doc.statut} />}
      {doc.statut === 'ouvert' && (
        <button onClick={handleRecalculer} disabled={recalculating}
          data-testid="recalculer-btn"
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
          style={{ background: 'var(--primary-solid)' }}>
          {recalculating ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          Recalculer le coût de revient
        </button>
      )}
    </div>
  ) : null;

  return (
    <SidePanel open={open} onOpenChange={v => !v && onClose()}
      title={doc ? doc.numero : 'Dossier de revient'}
      copyValue={doc?.numero}
      headerExtra={doc && <StatutBadge statut={doc.statut} />}
      size="lg" footer={footer} noPadding>

      {loading ? (
        <div className="flex justify-center items-center h-40">
          <Loader2 size={24} className="animate-spin" style={{ color: 'var(--primary-solid)' }} />
        </div>
      ) : doc && (
        <div className="overflow-y-auto h-full">
          {/* Libellé éditable */}
          <div className="px-6 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--border-light)', background: '#FAFAF8' }}>
            <input value={libelle}
              onChange={e => setLibelle(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && saveLibelle()}
              disabled={doc.statut !== 'ouvert'}
              placeholder="Libellé du dossier…"
              className="flex-1 text-sm font-medium rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-60 disabled:cursor-default"
              style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'white' }} />
            {doc.statut === 'ouvert' && libelle !== (doc.libelle || '') && (
              <button onClick={saveLibelle}
                className="px-3 py-2 rounded-lg text-xs font-semibold text-white flex-shrink-0"
                style={{ background: 'var(--primary-solid)' }}>
                Enregistrer
              </button>
            )}
          </div>

          {/* ── Bandeau synthèse (toujours visible) ── */}
          <div className="grid grid-cols-4 border-b" style={{ borderColor: 'var(--border-light)' }}>
            {[
              { label: 'Achats Purs',           val: computed.byTheme?.ACHATS_PURS,       pctVal: null,  color: '#1D4ED8', bg: '#EFF6FF' },
              { label: 'Frais d\'Approche',      val: computed.byTheme?.FRAIS_APPROCHE,    pctVal: pct(computed.byTheme?.FRAIS_APPROCHE, computed.byTheme?.ACHATS_PURS),   color: '#C2410C', bg: '#FFF7ED' },
              { label: 'Frais de Préparation',   val: computed.byTheme?.FRAIS_PREPARATION, pctVal: pct(computed.byTheme?.FRAIS_PREPARATION, computed.byTheme?.ACHATS_PURS), color: '#15803D', bg: '#F0FDF4' },
              { label: 'Coût Revient Total',     val: computed.total,                      pctVal: null,  color: 'var(--text)', bg: '#FAFAF8' },
            ].map((k, i) => (
              <div key={i} className="px-4 py-3 border-r last:border-r-0 text-center" style={{ background: k.bg, borderColor: 'var(--border-light)' }}>
                <p className="text-xs font-semibold mb-1" style={{ color: k.color }}>{k.label}</p>
                <p className="text-base font-bold tabular-nums" style={{ color: k.color }}>{fmt(k.val)}</p>
                {k.pctVal && <p className="text-xs mt-0.5 font-semibold" style={{ color: k.color }}>({k.pctVal})</p>}
              </div>
            ))}
          </div>

          {/* ── Onglets ── */}
          <div className="flex border-b px-6 gap-1" style={{ borderColor: 'var(--border-light)', background: '#FAFAF8' }}>
            {[
              { key: 'factures',     label: 'Factures' },
              { key: 'prix_revient', label: 'Prix de revient' },
            ].map(t => (
              <button key={t.key} onClick={() => setPanelTab(t.key)}
                className="px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors"
                style={{
                  borderColor: panelTab === t.key ? 'var(--primary-solid)' : 'transparent',
                  color: panelTab === t.key ? 'var(--primary-solid)' : 'var(--text-muted)',
                }}>
                {t.label}
              </button>
            ))}
          </div>

          {/* ── Onglet Factures ── */}
          {panelTab === 'factures' && (
            <div className="px-6 pt-5 pb-3">
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-muted)' }}>
                Factures d'achat rattachées
              </p>
              {Object.entries(THEMES).map(([thKey, th]) => {
                const facs = (doc.factures || []).filter(f => th.types.includes(f.type_charge));
                const subtotal = facs.reduce((s, f) => s + Number(f.montant_ht || 0), 0);
                return (
                  <div key={thKey} className="mb-4">
                    <div className="flex items-center justify-between px-3 py-1.5 rounded-t-lg text-xs font-bold"
                      style={{ background: th.bg, borderLeft: `3px solid ${th.color}`, color: th.color }}>
                      <span>{th.label}</span>
                      {facs.length > 0 && (
                        <span className="tabular-nums">
                          {fmt(subtotal)}
                          {thKey !== 'ACHATS_PURS' && computed.byTheme?.ACHATS_PURS > 0 && (
                            <span className="ml-2 font-normal opacity-70">
                              ({pct(subtotal, computed.byTheme?.ACHATS_PURS)})
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                    <table className="w-full text-xs border border-t-0 rounded-b-lg overflow-visible" style={{ borderColor: th.border }}>
                      <thead>
                        <tr style={{ background: '#FAFAF8', color: 'var(--text-muted)', borderBottom: `1px solid ${th.border}` }}>
                          <th className="text-left px-3 py-1.5 font-semibold">N° · Date</th>
                          <th className="text-left px-3 py-1.5 font-semibold">Fournisseur</th>
                          <th className="text-right px-3 py-1.5 font-semibold">HT (€)</th>
                          {thKey !== 'ACHATS_PURS' && <th className="text-right px-3 py-1.5 font-semibold">% Achats</th>}
                          <th className="px-2 py-1.5" />
                        </tr>
                      </thead>
                      <tbody>
                        {facs.map(f => (
                          <tr key={f.facture_id} style={{ borderTop: `1px solid ${th.border}` }}>
                            <td className="px-3 py-2" style={{ color: 'var(--text)' }}>
                              <span className="font-medium">{f.numero_fournisseur || '—'}</span>
                              {f.date_facture && <span className="ml-2 text-xs" style={{ color: 'var(--text-muted)' }}>{new Date(f.date_facture).toLocaleDateString('fr-FR')}</span>}
                            </td>
                            <td className="px-3 py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>{f.entreprise_nom || '—'}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: 'var(--text)' }}>{fmt(f.montant_ht)}</td>
                            {thKey !== 'ACHATS_PURS' && (
                              <td className="px-3 py-2 text-right tabular-nums" style={{ color: th.color }}>
                                {pct(Number(f.montant_ht || 0), computed.byTheme?.ACHATS_PURS)}
                              </td>
                            )}
                            <td className="px-2 py-2 text-right">
                              {doc.statut === 'ouvert' && (
                                <button onClick={() => api.detacherFactureDossier(dossier_revient_id, f.facture_id).then(load)}
                                  className="p-1 rounded hover:bg-red-50 transition-colors" style={{ color: '#DC2626' }}>
                                  <X size={11} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                        {doc.statut === 'ouvert' && (
                          <FactureGhostRow dossierId={dossier_revient_id}
                            defaultType={th.types[0]}
                            colSpan={thKey !== 'ACHATS_PURS' ? 5 : 4}
                            onDone={load} />
                        )}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Onglet Prix de revient ── */}
          {panelTab === 'prix_revient' && (
            <div className="px-6 pb-6 pt-4 space-y-6">

              {/* Section Matériels */}
              <div>
                <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-muted)' }}>
                  Matériels
                </p>
                {(computed.ventilation || []).length === 0
                  ? <p className="text-xs py-2" style={{ color: 'var(--text-disabled)' }}>Aucun matériel lié au dossier.</p>
                  : (
                    <table className="w-full text-xs border rounded-lg overflow-visible" style={{ borderColor: 'var(--border-light)' }}>
                      <thead>
                        <tr style={{ background: '#FAFAF8', color: 'var(--text-muted)', borderBottom: '1px solid var(--border-light)' }}>
                          <th className="text-left px-3 py-2 font-semibold">N° Série</th>
                          <th className="text-left px-3 py-2 font-semibold">Désignation</th>
                          <th className="text-right px-3 py-2 font-semibold" style={{ color: '#1D4ED8' }}>Achats Purs</th>
                          <th className="text-right px-3 py-2 font-semibold" style={{ color: '#C2410C' }}>Frais Approche</th>
                          <th className="text-right px-3 py-2 font-semibold" style={{ color: '#15803D' }}>Préparation</th>
                          <th className="text-right px-3 py-2 font-semibold" style={{ color: 'var(--primary-solid)' }}>Nouveau PdR</th>
                          <th className="px-2 py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {(computed.ventilation || []).map(m => {
                          const delta = m.cout_revient > 0 ? ((m.cout_revient_calc ?? m.cout_revient) - m.cout_revient) : null;
                          return (
                            <tr key={m.materiel_id} style={{ borderTop: '1px solid var(--border-light)' }}>
                              <td className="px-3 py-2 font-mono text-xs" style={{ color: 'var(--text-muted)' }}>{m.numero_serie || '—'}</td>
                              <td className="px-3 py-2 font-medium" style={{ color: 'var(--text)' }}>{m.nom}</td>
                              <td className="px-3 py-2 text-right tabular-nums" style={{ color: '#1D4ED8' }}>{fmt(m.prix_achat)}</td>
                              <td className="px-3 py-2 text-right tabular-nums" style={{ color: '#C2410C' }}>
                                {m.approche > 0 ? <>{fmt(m.approche)} <span className="text-xs opacity-60">({pct(m.approche, m.prix_achat)})</span></> : '—'}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums" style={{ color: '#15803D' }}>
                                {m.preparation > 0 ? <>{fmt(m.preparation)} <span className="text-xs opacity-60">({pct(m.preparation, m.prix_achat)})</span></> : '—'}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums font-bold" style={{ color: 'var(--primary-solid)' }}>
                                {fmt(m.cout_revient_calc ?? m.cout_revient)}
                                {delta !== null && delta !== 0 && (
                                  <span className="ml-1.5 text-xs font-normal" style={{ color: delta > 0 ? '#DC2626' : '#047857' }}>
                                    ({delta > 0 ? '+' : ''}{fmt(delta)})
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-2 text-right">
                                {doc.statut === 'ouvert' && (
                                  <button onClick={() => api.lierMaterielsDossier(dossier_revient_id, { materiel_ids: [m.materiel_id], action: 'remove' }).then(load)}
                                    className="p-1 rounded hover:bg-red-50" style={{ color: '#DC2626' }}>
                                    <X size={11} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {(computed.ventilation || []).length > 0 && (
                          <tr style={{ borderTop: '2px solid var(--border-light)', background: '#FAFAF8' }}>
                            <td className="px-3 py-2 font-bold text-xs" style={{ color: 'var(--text-muted)' }} colSpan={2}>TOTAL</td>
                            <td className="px-3 py-2 text-right tabular-nums font-bold" style={{ color: '#1D4ED8' }}>{fmt(computed.byTheme?.ACHATS_PURS)}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-bold" style={{ color: '#C2410C' }}>{fmt(computed.byTheme?.FRAIS_APPROCHE)}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-bold" style={{ color: '#15803D' }}>{fmt(computed.byTheme?.FRAIS_PREPARATION)}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-bold" style={{ color: 'var(--primary-solid)' }}>{fmt(computed.total)}</td>
                            <td />
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )
                }
              </div>

              {/* Section Pièces détachées */}
              {computedPieces.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-muted)' }}>
                    Pièces détachées
                  </p>
                  <table className="w-full text-xs border rounded-lg overflow-visible" style={{ borderColor: 'var(--border-light)' }}>
                    <thead>
                      <tr style={{ background: '#FAFAF8', color: 'var(--text-muted)', borderBottom: '1px solid var(--border-light)' }}>
                        <th className="text-left px-3 py-2 font-semibold">Réf.</th>
                        <th className="text-left px-3 py-2 font-semibold">Désignation</th>
                        <th className="text-right px-3 py-2 font-semibold">Qté</th>
                        <th className="text-right px-3 py-2 font-semibold" style={{ color: '#1D4ED8' }}>Montant Achat</th>
                        <th className="text-right px-3 py-2 font-semibold" style={{ color: '#C2410C' }}>Frais imputés</th>
                        <th className="text-right px-3 py-2 font-semibold">PMP actuel</th>
                        <th className="text-right px-3 py-2 font-semibold" style={{ color: 'var(--primary-solid)' }}>Nouveau PMP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {computedPieces.map(p => {
                        const delta = p.pmp_actuel > 0 ? (p.nouveauPmp - p.pmp_actuel) : null;
                        return (
                          <tr key={p.ligne_id} style={{ borderTop: '1px solid var(--border-light)' }}>
                            <td className="px-3 py-2 font-mono text-xs" style={{ color: 'var(--text-muted)' }}>{p.reference || '—'}</td>
                            <td className="px-3 py-2 font-medium" style={{ color: 'var(--text)' }}>{p.designation}</td>
                            <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--text-secondary)' }}>{Number(p.quantite).toLocaleString('fr-FR')}</td>
                            <td className="px-3 py-2 text-right tabular-nums" style={{ color: '#1D4ED8' }}>{fmt(p.montant_ht)}</td>
                            <td className="px-3 py-2 text-right tabular-nums" style={{ color: '#C2410C' }}>
                              {p.fraisImputes > 0 ? fmt(p.fraisImputes) : '—'}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--text-secondary)' }}>
                              {p.pmp_actuel > 0 ? fmt(p.pmp_actuel) : '—'}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums font-bold" style={{ color: 'var(--primary-solid)' }}>
                              {fmt(p.nouveauPmp)}
                              {delta !== null && delta !== 0 && (
                                <span className="ml-1.5 text-xs font-normal" style={{ color: delta > 0 ? '#DC2626' : '#047857' }}>
                                  ({delta > 0 ? '+' : ''}{fmt(delta)})
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <p className="mt-2 text-xs" style={{ color: 'var(--text-placeholder)' }}>
                    Nouveau PMP = (PMP actuel × stock + montant achat + frais imputés) / (stock + qté reçue). Appliqué au clic sur "Recalculer le coût de revient".
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </SidePanel>
  );
}


// ─── Ghost row rattachement facture ──────────────────────────────────────────
function FactureGhostRow({ dossierId, defaultType, colSpan, onDone }) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [pos, setPos]         = useState(null);
  const inputRef = useRef();
  const dropRef  = useRef();

  useEffect(() => {
    const h = (e) => {
      if (inputRef.current?.contains(e.target)) return;
      if (dropRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.getFacturesDisponiblesDR(dossierId, { search: query || undefined });
        setResults(r.data || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open, dossierId]);

  const calcPos = () => {
    if (!inputRef.current) return;
    const r = inputRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 500) });
  };

  const handleSelect = async (f) => {
    setOpen(false); setQuery(''); setResults([]);
    setSaving(true);
    try {
      await api.lierFactureDossier(dossierId, { facture_id: f.facture_id, type_charge: defaultType });
      toast.success('Facture rattachée');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <tr style={{ borderTop: '1px solid var(--border-light)' }}>
        <td colSpan={colSpan} className="px-3 py-1.5">
          <div className="flex items-center gap-2 rounded-lg border px-2.5 py-1 focus-within:ring-1 focus-within:ring-blue-300"
            style={{ borderColor: 'var(--border)', background: '#fff' }}>
            <Plus size={12} style={{ color: '#9CA3AF', flexShrink: 0 }} />
            <Search size={12} style={{ color: '#9CA3AF', flexShrink: 0 }} />
            <input ref={inputRef} value={query}
              onChange={e => { setQuery(e.target.value); calcPos(); setOpen(true); }}
              onFocus={() => { calcPos(); setOpen(true); }}
              placeholder="Rechercher une facture d'achat…"
              data-testid="facture-ghost-search"
              className="flex-1 text-xs bg-transparent focus:outline-none py-0.5"
              style={{ color: 'var(--text)' }} />
            {(loading || saving) && <Loader2 size={12} className="animate-spin" style={{ color: '#9CA3AF' }} />}
          </div>
        </td>
      </tr>
      {open && pos && (
        <tr style={{ display: 'contents' }}>
          <td>
            <div ref={dropRef}
              style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width,
                       background: 'white', border: '1px solid var(--border)',
                       borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                       maxHeight: 260, overflowY: 'auto', zIndex: 9999 }}>
              {results.length === 0 ? (
                <div className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                  {loading ? 'Chargement…' : 'Aucune facture disponible'}
                </div>
              ) : results.map(f => (
                <button key={f.facture_id} type="button"
                  onMouseDown={e => { e.preventDefault(); handleSelect(f); }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-0 transition-colors flex items-center gap-3"
                  style={{ borderColor: 'var(--border-light)' }}>
                  <span className="font-semibold text-xs flex-shrink-0" style={{ color: 'var(--text)', minWidth: 90 }}>
                    {f.numero_fournisseur || <span style={{ color: 'var(--text-disabled)', fontWeight: 400 }}>Sans n°</span>}
                  </span>
                  <span className="text-xs flex-1 truncate" style={{ color: 'var(--text-secondary)' }}>{f.entreprise_nom || '—'}</span>
                  <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
                    {f.date_facture ? new Date(f.date_facture).toLocaleDateString('fr-FR') : '—'}
                  </span>
                  <span className="text-xs font-bold tabular-nums flex-shrink-0" style={{ color: 'var(--primary-solid)', minWidth: 70, textAlign: 'right' }}>
                    {Number(f.montant_ht) > 0 ? fmt(f.montant_ht) : '—'}
                  </span>
                </button>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}


// ─── Page principale ──────────────────────────────────────────────────────────
export default function DossiersRevient() {
  const { activeAgences } = useSociete();
  const agenceUnique = activeAgences?.length === 1 ? activeAgences[0] : null;

  const [dossiers, setDossiers]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');
  const [showCreate, setShowCreate]     = useState(false);
  const [selected, setSelected]         = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getDossiersRevient({
        search: search || undefined,
        statut: filtreStatut || undefined,
      });
      const d = r.data;
      setDossiers(d.data || []);
      setTotal(d.total || 0);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [search, filtreStatut]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Dossiers de revient</h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Coût de revient réel par arrivage — frais d'approche imputés sur les matériels
          </p>
        </div>
        <button onClick={() => agenceUnique && setShowCreate(true)}
          disabled={!agenceUnique}
          title={!agenceUnique ? 'Sélectionnez une agence unique pour créer un dossier' : ''}
          data-testid="create-dossier-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: 'var(--primary-solid)' }}>
          <Plus size={14} /> Nouveau dossier
        </button>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1" style={{ maxWidth: 320 }}>
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher numéro, libellé…"
            data-testid="search-dossiers"
            className="w-full pl-9 py-2.5 rounded-xl border text-sm focus:outline-none"
            style={{ borderColor: 'var(--border)' }} />
        </div>
        <select value={filtreStatut} onChange={e => setFiltreStatut(e.target.value)}
          className="border rounded-xl px-3 py-2.5 text-sm focus:outline-none"
          style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
          <option value="">Tous statuts</option>
          <option value="ouvert">Ouverts</option>
          <option value="cloture">Clôturés</option>
        </select>
        <button onClick={load} disabled={loading}
          className="p-2.5 rounded-xl border hover:bg-gray-50 disabled:opacity-60"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 size={24} className="animate-spin" style={{ color: 'var(--primary-solid)' }} />
        </div>
      ) : dossiers.length === 0 ? (
        <div className="bg-white rounded-xl border p-16 text-center" style={{ borderColor: 'var(--border)' }}>
          <Package size={40} className="mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
          <p className="font-semibold mb-1" style={{ color: 'var(--text)' }}>Aucun dossier de revient</p>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Créez un dossier pour un arrivage et imputez les frais d'approche sur vos matériels.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs font-semibold uppercase tracking-wide"
                style={{ background: '#FAFAF8', color: 'var(--text-muted)', borderBottom: '1px solid var(--border-light)' }}>
                <th className="text-left px-4 py-3">Numéro</th>
                <th className="text-left px-4 py-3">Libellé</th>
                <th className="text-center px-4 py-3">Statut</th>
                <th className="text-center px-4 py-3">Clé</th>
                <th className="text-right px-4 py-3">Matériels</th>
                <th className="text-right px-4 py-3">Total frais</th>
                <th className="text-left px-4 py-3">Date arrivée</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {dossiers.map(d => (
                <tr key={d.dossier_revient_id}
                  data-testid={`dossier-row-${d.dossier_revient_id}`}
                  className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                  style={{ borderColor: 'var(--border-light)' }}
                  onClick={() => setSelected(d.dossier_revient_id)}>
                  <td className="px-4 py-3 font-mono font-semibold text-xs" style={{ color: 'var(--primary-solid)' }}>
                    {d.numero}
                  </td>
                  <td className="px-4 py-3 font-medium" style={{ color: 'var(--text)' }}>{d.libelle || '—'}</td>
                  <td className="px-4 py-3 text-center"><StatutBadge statut={d.statut} /></td>
                  <td className="px-4 py-3 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                    {d.cle_repartition === 'VALEUR' ? 'Ad Valorem' : 'Égalitaire'}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums" style={{ color: 'var(--text-secondary)' }}>
                    {d.nb_materiels ?? 0}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold" style={{ color: '#D97706' }}>
                    {fmt(d.total_frais)}
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                    {d.date_arrivee ? new Date(d.date_arrivee).toLocaleDateString('fr-FR') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 text-xs text-right" style={{ color: 'var(--text-disabled)', borderTop: '1px solid var(--border-light)' }}>
            {total} dossier{total > 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreate && (
        <CreateDossierPanel open={showCreate} onClose={() => setShowCreate(false)}
          onCreated={(d) => { setShowCreate(false); load(); setSelected(d.dossier_revient_id); }}
        />
      )}
      {selected && (
        <DossierPanel dossier_revient_id={selected} open={!!selected}
          onClose={() => setSelected(null)} onRefresh={load}
        />
      )}
    </div>
  );
}
